import React from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import PropTypes from 'prop-types';
import { CHART_TEXT_COLOR, CHART_AXIS_COLOR, CHART_GRID_COLOR } from '../../utils/theme';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

/**
 * LineChart component for displaying time series data
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Chart data object with labels and datasets
 * @param {string} [props.title] - Optional chart title
 * @param {Object} [props.options] - Additional chart options
 * @returns {JSX.Element} Rendered component
 */
const LineChart = ({ data, title, options = {} }) => {
  // Basic check for data
  if (!data) {
    return <div className="flex items-center justify-center h-full text-gray-600 font-medium">No data available</div>;
  }

  // Handle different data formats
  let chartData;
  
  if (data.labels && data.datasets) {
    // Data is already in the right format
    chartData = data;
    
    // Check if datasets array is empty or contains no data points
    if (!data.datasets.length || !data.labels.length || !data.datasets[0].data.length) {
      return <div className="flex items-center justify-center h-full text-gray-600 font-medium">No yearly data available</div>;
    }
  } else if (Array.isArray(data) && data.length > 0) {
    // Convert array of objects to chart format
    if ('year' in data[0] && 'value' in data[0]) {
      const sortedData = [...data].sort((a, b) => a.year.localeCompare(b.year));
      
      chartData = {
        labels: sortedData.map(item => item.year),
        datasets: [{
          label: 'Funding',
          data: sortedData.map(item => item.value),
          borderColor: 'rgba(56, 189, 248, 1)',
          backgroundColor: 'rgba(56, 189, 248, 0.2)',
          borderWidth: 2,
          tension: 0.1
        }]
      };
    } else {
      return <div className="flex items-center justify-center h-full text-gray-600 font-medium">Incompatible data format</div>;
    }
  } else {
    return <div className="flex items-center justify-center h-full text-gray-600 font-medium">No yearly data available</div>;
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: !!title,
        text: title || '',
        font: {
          size: 14,
          weight: 'bold'
        },
        color: CHART_TEXT_COLOR,
        padding: {
          top: 10,
          bottom: 20
        }
      },
      legend: {
        position: 'top',
        labels: {
          boxWidth: 12,
          padding: 10,
          color: CHART_TEXT_COLOR,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: $${context.raw.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: CHART_AXIS_COLOR,
          callback: function(value) {
            return '$' + value.toLocaleString();
          }
        },
        grid: {
          color: CHART_GRID_COLOR
        }
      },
      x: {
        ticks: {
          color: CHART_AXIS_COLOR,
        },
        grid: {
          color: CHART_GRID_COLOR
        }
      }
    },
    ...options
  };

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <Line data={chartData} options={chartOptions} />
    </div>
  );
};

LineChart.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.shape({
      labels: PropTypes.array.isRequired,
      datasets: PropTypes.array.isRequired
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        year: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired
      })
    )
  ]).isRequired,
  title: PropTypes.string,
  options: PropTypes.object
};

export default LineChart;